import { useMemo, useState } from 'react';
import { Copy, EyeOff, Ruler, ScanSearch, ShieldX } from 'lucide-react';

import { useAssetQuery } from '@/client/api/assets/assets.query';
import {
  KIND_LABELS,
  type DuplicateGroup,
  useDuplicateGroupsQuery,
} from '@/client/api/assets/duplicates';
import type { ModerationAction } from '@/client/api/assets/moderation';
import AssetImage from '@/client/components/admin/Assets/AssetImage';
import JobLauncherDialog from '@/client/components/admin/Assets/JobLauncherDialog';
import MatchStolenDialog from '@/client/components/admin/Assets/MatchStolenDialog';
import ModerationDialog from '@/client/components/admin/Assets/ModerationDialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const KIND_CLS = {
  cross_owner: 'bg-red-50 text-red-700',
  same_owner_multi_listing: 'bg-amber-50 text-amber-800',
  same_listing: 'bg-gray-100 text-gray-600',
};
const PAGE = 20;
const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

/**
 * Duplicates & provenance: the same photo (perceptual hash) appearing more
 * than once, worst first — the same image under two sellers is how a stolen
 * listing looks. Pick the copies in a group and hide or reject them; the
 * original's seller keeps theirs.
 */
export const DuplicatesPage = () => {
  useBreadcrumbs([{ label: 'Asset Management', href: '/admin/assets' }, { label: 'Duplicates' }]);
  const [match, setMatch] = useState<'exact' | 'near'>('exact');
  const [distance, setDistance] = useState(10);
  const [crossOnly, setCrossOnly] = useState(false);
  const [page, setPage] = useState(0);
  const { data, isLoading, isError, error, isFetching } = useDuplicateGroupsQuery(match, distance, match === 'near' ? true : crossOnly);
  const groups = data?.groups ?? [];
  const visible = groups.slice(page * PAGE, page * PAGE + PAGE);

  // One Library call for every photo on this page of groups.
  const ids = useMemo(() => Array.from(new Set(visible.flatMap((g) => g.asids))).slice(0, 500), [visible]);
  const { data: rows } = useAssetQuery(1, { page_size: 100, asids: ids.join(',') });
  const byId = useMemo(() => new Map((rows?.assets ?? []).map((a) => [a.asid, a])), [rows]);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dialog, setDialog] = useState<ModerationAction | null>(null);
  const [lookup, setLookup] = useState(false);
  const [measure, setMeasure] = useState(false);
  const { hasPermission: canModerate, isSuperAdmin } = useHasPermission('asset.moderate');
  const { hasPermission: canRun } = useHasPermission('asset.run');
  const cov = data?.coverage;

  const toggle = (asid: number) => setSelected((s) => { const n = new Set(s); n.has(asid) ? n.delete(asid) : n.add(asid); return n; });

  const Group = ({ g }: { g: DuplicateGroup }) => (
    <div className="rounded-lg border bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span className={cn('rounded-full px-2 py-0.5 font-semibold', KIND_CLS[g.kind])}>{KIND_LABELS[g.kind]}</span>
        <span className="text-gray-500">{g.asset_count} photos · {g.owners.length} owner{g.owners.length === 1 ? '' : 's'} · {g.eids.length} listing{g.eids.length === 1 ? '' : 's'}</span>
        <span className="font-mono text-gray-400">{g.phash}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {g.asids.map((asid) => {
          const a = byId.get(asid);
          return (
            <label key={asid} className={cn('w-36 shrink-0 cursor-pointer overflow-hidden rounded border', selected.has(asid) && 'ring-2 ring-blue-500')}>
              <div className="relative aspect-square bg-muted">
                {a ? <AssetImage asset={a} className="h-full w-full object-cover" /> : <Skeleton className="h-full w-full" />}
                {(canModerate || isSuperAdmin) && (
                  <input type="checkbox" className="absolute left-1.5 top-1.5" checked={selected.has(asid)} onChange={() => toggle(asid)} />
                )}
              </div>
              <div className="p-1.5 text-[11px] text-gray-600">
                <a className="text-blue-600 hover:underline" href={`/admin/assets?q=${asid}`} target="_blank" rel="noreferrer">#{asid}</a>
                {a?.eid ? ` · listing #${a.eid}` : ''}
                <div className="truncate">{a?.oid ? `org #${a.oid}` : a?.uid ? `user #${a.uid}` : ''}{a?.date_added ? ` · ${new Date(a.date_added).toLocaleDateString()}` : ''}</div>
                {a?.moderation?.status && <div className="font-medium capitalize text-orange-700">{a.moderation.status}</div>}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen space-y-5 bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Duplicates</h1>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">
              The same photo more than once, by perceptual hash. The same image under two sellers is how a copied listing looks;
              the oldest upload is usually the original.
            </p>
          </div>
          <Button size="sm" onClick={() => setLookup(true)}><ScanSearch className="mr-2 h-4 w-4" /> Reverse-image lookup</Button>
        </div>

        {cov && (
          <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-white px-4 py-3 text-sm">
            <span><b>{cov.hashed.toLocaleString()}</b> of {cov.images.toLocaleString()} live images hashed ({pct(cov.hashed, cov.images)}); only hashed photos can match.</span>
            <span className="text-gray-500">{cov.measured.toLocaleString()} measured for local metrics ({pct(cov.measured, cov.images)}).</span>
            {(canRun || isSuperAdmin) && cov.measured < cov.images && (
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => setMeasure(true)}>
                <Ruler className="mr-1.5 h-3.5 w-3.5" /> Measure the rest…
              </Button>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-sm">
          {(['exact', 'near'] as const).map((m) => (
            <button key={m} onClick={() => { setMatch(m); setPage(0); setSelected(new Set()); }}
              className={cn('rounded-full border px-3 py-1', match === m ? 'border-gray-900 bg-gray-900 text-white' : 'bg-white')}>
              {m === 'exact' ? 'Identical' : 'Near (crops, resizes, watermarks)'}
            </button>
          ))}
          {match === 'near' ? (
            <label className="flex items-center gap-2">Tolerance
              <input type="range" min={2} max={16} value={distance} onChange={(e) => { setDistance(Number(e.target.value)); setPage(0); }} />
              <span className="w-6 tabular-nums">{distance}</span><span className="text-xs text-gray-500">bits · different sellers only</span>
            </label>
          ) : (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={crossOnly} onChange={(e) => { setCrossOnly(e.target.checked); setPage(0); }} /> Different sellers only
            </label>
          )}
          {data && (
            <span className="ml-auto text-xs text-gray-500">
              {data.group_count} groups{Object.entries(data.kinds).map(([k, n]) => ` · ${KIND_LABELS[k as keyof typeof KIND_LABELS]} ${n}`).join('')}
              {isFetching && ' · refreshing…'}
            </span>
          )}
        </div>

        {selected.size > 0 && (
          <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm shadow-sm">
            <span className="font-medium">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={() => setDialog('hide')}><EyeOff className="mr-1.5 h-3.5 w-3.5" /> Hide copies</Button>
            <Button size="sm" variant="destructive" onClick={() => setDialog('reject')}><ShieldX className="mr-1.5 h-3.5 w-3.5" /> Reject copies</Button>
            <button className="ml-auto text-blue-700 hover:underline" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        {isLoading ? <Skeleton className="h-96" /> : isError ? (
          <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not load duplicates')}</p>
        ) : !groups.length ? (
          <div className="rounded-lg border-2 border-dashed bg-white px-4 py-16 text-center text-sm text-gray-500">
            <Copy className="mx-auto mb-2 h-8 w-8 text-gray-300" /> No duplicate groups here.
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((g) => <Group key={g.phash + g.asids[0]} g={g} />)}
            <div className="flex items-center justify-between text-sm">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="text-gray-500">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, groups.length)} of {groups.length}{data?.truncated ? '+' : ''}</span>
              <Button size="sm" variant="outline" disabled={(page + 1) * PAGE >= groups.length} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <ModerationDialog
        action={dialog}
        asids={Array.from(selected)}
        suggestedReason="duplicate_or_stolen"
        onClose={() => setDialog(null)}
        onDone={() => setSelected(new Set())}
      />
      <MatchStolenDialog open={lookup} onClose={() => setLookup(false)} />
      <JobLauncherDialog
        open={measure}
        onClose={() => setMeasure(false)}
        scope={{ filter: { type: 'image', state: 'active' } }}
        scopeLabel="every live image (already-measured ones are quick)"
        initial={{ kind: 'bulk', action: 'measure' }}
      />
    </div>
  );
};

export default DuplicatesPage;
